import React from "react";
import type { SetURLSearchParams } from "react-router-dom";
import type { ApiClient } from "../../../app/api/client";
import {
MEMBER_APP_REQUEST_TYPE_CANCELLATION,
MEMBER_APP_REQUEST_TYPE_REST_PERIOD
} from "../../../app/constants/memberAppRequestType";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import type { PersonDto } from "../types";
import type { ContractRow,RestPeriodRow } from "./personDetailTypes";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

type MemberAppRequestDto = {
  id?: string;
  type?: number;
  status?: number;
  contractPackageId?: string | null;
  note?: string | null;
  requestedEffectiveDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

type UseMemberAppRequestDrawerArgs = {
  api: ApiClient;
  message: Feedback;
  text: I18nContextValue["text"];
  requestId?: string;
  personId: string;
  person: PersonDto | null;
  waitsForPerson: boolean;
  canCancelContracts: boolean;
  canWriteRestPeriods: boolean;
  setSearchParams: SetURLSearchParams;
  setMemberAppRequestIdForDrawer: (value: string | null) => void;
  setContractCancelItem: (value: ContractRow | null) => void;
  setCancelPrefillFromRequest: (value: { requestedEffectiveDate: string } | null) => void;
  setContractCancelOpen: (value: boolean) => void;
  setRestEdit: (value: RestPeriodRow | null) => void;
  setRestPrefillFromRequest: (value: {
    contractPackageId: string;
    startDate?: string;
    endDate?: string;
    note?: string;
  } | null) => void;
  setRestOpen: (value: boolean) => void;
};

export function useMemberAppRequestDrawer({
  api,
  message,
  text,
  requestId,
  personId,
  person,
  waitsForPerson,
  canCancelContracts,
  canWriteRestPeriods,
  setSearchParams,
  setMemberAppRequestIdForDrawer,
  setContractCancelItem,
  setCancelPrefillFromRequest,
  setContractCancelOpen,
  setRestEdit,
  setRestPrefillFromRequest,
  setRestOpen
}: UseMemberAppRequestDrawerArgs) {
  React.useEffect(() => {
    if (!requestId || !personId) return;
    if (waitsForPerson && !person) return;

    let alive = true;
    const showMissingContract = () => {
      if (alive) {
        message.error(text("The requested contract could not be found. It may have been deleted or is not assigned to this person."));
      }
    };

    (async () => {
      try {
        const request = await api.get<MemberAppRequestDto>(`/api/v1/studio-app-requests/${encodeURIComponent(requestId)}`);
        if (!alive || !request || typeof request !== "object") return;
        if (Number(request.status) !== 0) return;

        const reqId = request.id != null ? String(request.id) : null;
        const reqType = Number(request.type);
        const contractPackageId = request.contractPackageId != null ? String(request.contractPackageId) : null;

        if (reqId && reqType === MEMBER_APP_REQUEST_TYPE_CANCELLATION) {
          setMemberAppRequestIdForDrawer(reqId);
        }
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("memberAppRequestId");
          next.set("tab", reqType === MEMBER_APP_REQUEST_TYPE_REST_PERIOD ? "restPeriods" : "contracts");
          return next;
        });

        if (reqType === MEMBER_APP_REQUEST_TYPE_CANCELLATION) {
          if (!canCancelContracts) return;
          if (!contractPackageId) {
            showMissingContract();
            return;
          }

          const packages = await api.get<ContractRow[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`);
          if (!alive) return;
          const row = Array.isArray(packages) ? packages.find((contract) => String(contract.id) === contractPackageId) : undefined;
          if (!row || typeof row !== "object") {
            showMissingContract();
            return;
          }

          setContractCancelItem(row as ContractRow);
          if (request.requestedEffectiveDate) {
            setCancelPrefillFromRequest({ requestedEffectiveDate: String(request.requestedEffectiveDate).slice(0, ISO_DATE_LENGTH) });
          }
          setContractCancelOpen(true);
          return;
        }

        if (reqType === MEMBER_APP_REQUEST_TYPE_REST_PERIOD) {
          if (!canWriteRestPeriods || !contractPackageId) return;
          if (reqId) {
            setMemberAppRequestIdForDrawer(reqId);
          }
          setRestEdit(null);
          setRestPrefillFromRequest({
            contractPackageId,
            startDate: request.startDate != null ? String(request.startDate).slice(0, ISO_DATE_LENGTH) : undefined,
            endDate: request.endDate != null ? String(request.endDate).slice(0, ISO_DATE_LENGTH) : undefined,
            note: request.note ?? undefined
          });
          setRestOpen(true);
        }
      } catch {
        if (alive) {
          message.error(text("The task could not be loaded. Please try again or open the person directly."));
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    api,
    canCancelContracts,
    canWriteRestPeriods,
    message,
    person,
    personId,
    requestId,
    setCancelPrefillFromRequest,
    setContractCancelItem,
    setContractCancelOpen,
    setMemberAppRequestIdForDrawer,
    setRestEdit,
    setRestOpen,
    setRestPrefillFromRequest,
    setSearchParams,
    text,
    waitsForPerson
  ]);
}
